import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Text,
  VStack,
  HStack,
  Heading,
  Switch,
  Divider,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  useDisclosure,
  Flex,
  Badge,
  Link,
  Collapse,
  IconButton,
  useColorModeValue,
} from "@chakra-ui/react";
import {
  FaCookie,
  FaShieldAlt,
  FaChartLine,
  FaUsers,
  FaGlobe,
  FaCog,
  FaChevronDown,
  FaChevronUp,
  FaExternalLinkAlt,
} from "react-icons/fa";

const ModernCookieConsent = () => {
  const [showBanner, setShowBanner] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const { isOpen, onOpen, onClose } = useDisclosure();

  const [cookiePreferences, setCookiePreferences] = useState({
    necessary: true, // Always true, can't be disabled
    analytics: false,
  });

  const bgColor = useColorModeValue("white", "gray.800");
  const borderColor = useColorModeValue("gray.200", "gray.600");
  const shadowColor = useColorModeValue(
    "rgba(0, 0, 0, 0.1)",
    "rgba(0, 0, 0, 0.3)"
  );

  useEffect(() => {
    // Check if user has already made a choice
    const hasConsent = localStorage.getItem("cookieConsent");
    if (!hasConsent) {
      // Delay showing banner for better UX
      const timer = setTimeout(() => {
        setShowBanner(true);
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, []);

  const trackingScripts = (preferences) => {
    // Implement your tracking codes here based on preferences
    console.log("Tracking scripts enabled for:", preferences);

    if (preferences.analytics) {
      // Enable Google Analytics, etc.
      console.log("Analytics enabled");
    }
  };

  const handleAcceptAll = () => {
    const allPreferences = {
      necessary: true,
      analytics: true,
    };

    setCookiePreferences(allPreferences);
    localStorage.setItem("cookieConsent", JSON.stringify(allPreferences));
    localStorage.setItem("cookieConsentDate", new Date().toISOString());

    trackingScripts(allPreferences);
    setShowBanner(false);
  };

  const handleAcceptSelected = () => {
    localStorage.setItem("cookieConsent", JSON.stringify(cookiePreferences));
    localStorage.setItem("cookieConsentDate", new Date().toISOString());

    trackingScripts(cookiePreferences);
    setShowBanner(false);
    onClose();
  };

  const handleDeclineAll = () => {
    const minimalPreferences = {
      necessary: true,
      analytics: false,
    };

    setCookiePreferences(minimalPreferences);
    localStorage.setItem("cookieConsent", JSON.stringify(minimalPreferences));
    localStorage.setItem("cookieConsentDate", new Date().toISOString());

    setShowBanner(false);
  };

  const cookieTypes = [
    {
      id: "necessary",
      title: "Necesare",
      description:
        "Aceste cookie-uri sunt esențiale pentru funcționarea site-ului și nu pot fi dezactivate.",
      icon: FaShieldAlt,
      color: "green",
      required: true,
      examples:
        "Sesiuni de utilizator, preferințe de limbă, setări de securitate",
    },
    {
      id: "analytics",
      title: "Analiză",
      description:
        "Ne ajută să înțelegem cum interactionați cu site-ul nostru pentru îmbunătățiri.",
      icon: FaChartLine,
      color: "blue",
      required: false,
      examples: "Google Analytics, statistici de utilizare, harta de căldură",
    },
  ];

  if (!showBanner) return null;

  return (
    <>
      {/* Cookie Banner */}
      <Box
        position="fixed"
        bottom="0"
        left="0"
        right="0"
        bg={bgColor}
        borderTop="1px solid"
        borderColor={borderColor}
        boxShadow={`0 -4px 20px ${shadowColor}`}
        zIndex="9999"
        p={6}
        backdropFilter="blur(10px)"
      >
        <Box maxW="container.xl" mx="auto">
          <Flex
            direction={{ base: "column", lg: "row" }}
            align={{ base: "start", lg: "center" }}
            gap={6}
          >
            {/* Cookie Icon and Content */}
            <Flex flex="1" gap={4} align="start">
              <Box
                p={3}
                borderRadius="xl"
                bg="blue.50"
                color="blue.500"
                flexShrink={0}
              >
                <FaCookie size={24} />
              </Box>

              <VStack align="start" spacing={2} flex="1">
                <Heading size="md" fontFamily="Montserrat" color="gray.800">
                  Respectăm confidențialitatea dvs.
                </Heading>
                <Text fontSize="sm" color="gray.600" lineHeight="tall">
                  Utilizăm cookie-uri pentru a optimiza funcționalitatea
                  site-ului și pentru a înțelege mai bine modul în care este
                  utilizat. Alegeți dacă doriți să activați cookie-urile de
                  analiză.
                </Text>

                <Collapse in={showDetails}>
                  <Box mt={4} p={4} bg="gray.50" borderRadius="md" w="full">
                    <VStack spacing={3} align="start">
                      {cookieTypes.map((type) => (
                        <HStack key={type.id} spacing={3} w="full">
                          <Box color={`${type.color}.500`}>
                            <type.icon size={16} />
                          </Box>
                          <VStack align="start" spacing={1} flex="1">
                            <HStack>
                              <Text
                                fontSize="sm"
                                fontWeight="600"
                                color="gray.700"
                              >
                                {type.title}
                              </Text>
                              {type.required && (
                                <Badge colorScheme="green" size="sm">
                                  Obligatoriu
                                </Badge>
                              )}
                            </HStack>
                            <Text fontSize="xs" color="gray.600">
                              {type.description}
                            </Text>
                          </VStack>
                          <Switch
                            isChecked={cookiePreferences[type.id]}
                            isDisabled={type.required}
                            onChange={(e) =>
                              setCookiePreferences((prev) => ({
                                ...prev,
                                [type.id]: e.target.checked,
                              }))
                            }
                            colorScheme={type.color}
                            size="sm"
                          />
                        </HStack>
                      ))}
                    </VStack>
                  </Box>
                </Collapse>
              </VStack>
            </Flex>

            {/* Action Buttons */}
            <VStack
              spacing={3}
              align="stretch"
              minW={{ base: "full", lg: "300px" }}
            >
              <HStack spacing={3}>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => setShowDetails(!showDetails)}
                  rightIcon={showDetails ? <FaChevronUp /> : <FaChevronDown />}
                  flex="1"
                >
                  {showDetails ? "Mai puțin" : "Personalizează"}
                </Button>
                <Button size="sm" variant="outline" onClick={onOpen} flex="1">
                  Detalii
                </Button>
              </HStack>

              <HStack spacing={3}>
                <Button
                  size="md"
                  variant="outline"
                  onClick={handleDeclineAll}
                  flex="1"
                  fontFamily="Montserrat"
                >
                  Doar necesare
                </Button>
                <Button
                  size="md"
                  colorScheme="blue"
                  onClick={showDetails ? handleAcceptSelected : handleAcceptAll}
                  flex="1"
                  fontFamily="Montserrat"
                  fontWeight="600"
                >
                  {showDetails ? "Acceptă selecția" : "Acceptă"}
                </Button>
              </HStack>
            </VStack>
          </Flex>
        </Box>
      </Box>

      {/* Detailed Cookie Policy Modal */}
      <Modal
        isOpen={isOpen}
        onClose={onClose}
        size="2xl"
        scrollBehavior="inside"
      >
        <ModalOverlay backdropFilter="blur(4px)" />
        <ModalContent>
          <ModalHeader>
            <HStack>
              <Box color="blue.500">
                <FaCookie size={24} />
              </Box>
              <Text fontFamily="Montserrat">Politica Cookie-urilor</Text>
            </HStack>
          </ModalHeader>
          <ModalCloseButton />

          <ModalBody pb={6}>
            <VStack spacing={6} align="start">
              <Text fontSize="sm" color="gray.600" lineHeight="tall">
                Cookie-urile sunt fișiere mici de text care sunt plasate pe
                computerul sau dispositivul mobil atunci când vizitați un site
                web. Ele sunt utilizate pe scară largă pentru a face ca
                site-urile web să funcționeze sau să funcționeze mai eficient,
                precum și pentru a furniza informații proprietarilor site-ului.
              </Text>

              <Divider />

              {/* Cookie Types Detailed */}
              <VStack spacing={6} w="full">
                {cookieTypes.map((type) => (
                  <Box
                    key={type.id}
                    w="full"
                    p={4}
                    borderRadius="lg"
                    bg="gray.50"
                  >
                    <VStack spacing={3} align="start">
                      <HStack>
                        <Box color={`${type.color}.500`}>
                          <type.icon size={20} />
                        </Box>
                        <Heading
                          size="sm"
                          color="gray.800"
                          fontFamily="Montserrat"
                        >
                          {type.title}
                        </Heading>
                        {type.required && (
                          <Badge colorScheme="green">Obligatoriu</Badge>
                        )}
                      </HStack>

                      <Text fontSize="sm" color="gray.600">
                        {type.description}
                      </Text>

                      <Box>
                        <Text
                          fontSize="xs"
                          color="gray.500"
                          fontWeight="500"
                          mb={1}
                        >
                          Exemple:
                        </Text>
                        <Text fontSize="xs" color="gray.500">
                          {type.examples}
                        </Text>
                      </Box>

                      <HStack justify="space-between" w="full">
                        <Text fontSize="sm" fontWeight="500" color="gray.700">
                          {type.required
                            ? "Activat"
                            : cookiePreferences[type.id]
                            ? "Activat"
                            : "Dezactivat"}
                        </Text>
                        <Switch
                          isChecked={cookiePreferences[type.id]}
                          isDisabled={type.required}
                          onChange={(e) =>
                            setCookiePreferences((prev) => ({
                              ...prev,
                              [type.id]: e.target.checked,
                            }))
                          }
                          colorScheme={type.color}
                        />
                      </HStack>
                    </VStack>
                  </Box>
                ))}
              </VStack>

              <Divider />

              {/* Additional Information */}
              <VStack spacing={4} align="start" w="full">
                <Heading size="sm" color="gray.800" fontFamily="Montserrat">
                  Informații suplimentare
                </Heading>

                <VStack spacing={2} align="start">
                  <Link
                    href="https://www.travel-time.ro/images/documente/Politica Cookies TT.pdf"
                    isExternal
                    color="blue.600"
                    fontSize="sm"
                    _hover={{ color: "blue.400" }}
                  >
                    <HStack>
                      <Text>Politica completă de Cookie-uri</Text>
                      <FaExternalLinkAlt size={12} />
                    </HStack>
                  </Link>

                  <Link
                    href="https://www.travel-time.ro/images/documente/Politica GDPR TT.pdf"
                    isExternal
                    color="blue.600"
                    fontSize="sm"
                    _hover={{ color: "blue.400" }}
                  >
                    <HStack>
                      <Text>Politica de Confidențialitate (GDPR)</Text>
                      <FaExternalLinkAlt size={12} />
                    </HStack>
                  </Link>
                </VStack>

                <Text fontSize="xs" color="gray.500" lineHeight="tall">
                  Pentru mai multe informații despre cum să gestionați
                  cookie-urile, vizitați{" "}
                  <Link
                    href="https://www.allaboutcookies.org"
                    isExternal
                    color="blue.500"
                  >
                    allaboutcookies.org
                  </Link>
                </Text>
              </VStack>

              {/* Action Buttons in Modal */}
              <HStack spacing={3} w="full" pt={4}>
                <Button variant="outline" onClick={handleDeclineAll} flex="1">
                  Doar necesare
                </Button>
                <Button
                  colorScheme="blue"
                  onClick={handleAcceptSelected}
                  flex="1"
                >
                  Salvează preferințele
                </Button>
              </HStack>
            </VStack>
          </ModalBody>
        </ModalContent>
      </Modal>
    </>
  );
};

export default ModernCookieConsent;
